import { CSSProperties, ReactNode } from "react";
import { messageIllustrations } from "../../../assets/illustrations";
import { cx } from "../../utils/cx";
import { Checkbox } from "../Checkbox";
import { Message } from "../Message";
import styles from "./Table.module.scss";

export type TableAlign = "left" | "center" | "right";

export interface TableColumn<T> {
    id: string;
    header: ReactNode;
    renderCell: (row: T) => ReactNode;
    width?: number | string;
    minWidth?: number | string;
    align?: TableAlign;
    className?: string;
}

export interface TableProps<T> {
    rows: T[];
    columns: TableColumn<T>[];
    getRowId: (row: T) => string;
    className?: string;
    selectable?: boolean;
    selectedRowIds?: string[];
    onSelectedRowIdsChange?: (rowIds: string[]) => void;
    onRowClick?: (row: T) => void;
    /**
     * When set, the table body scrolls inside this max height so the header
     * can stay pinned (`stickyHeader`) within the scroll box.
     */
    scrollMaxHeight?: string | number;
    /**
     * Pin header cells while an internal scroll box scrolls vertically.
     * Only useful with `scrollMaxHeight`. Default `false` — page-level
     * scroll (admin list screens) should not enable sticky thead.
     */
    stickyHeader?: boolean;
    /**
     * Empty-state title (string) or full custom content (ReactNode).
     * Strings use the DS `Message` pattern with the table illustration.
     * Default `"No data"`.
     */
    emptyMessage?: ReactNode;
    /** Optional supporting copy under the empty title (string `emptyMessage` only). */
    emptyDescription?: ReactNode;
    /**
     * Show the table/panel empty illustration when `emptyMessage` is a string.
     * Default `true`.
     */
    showEmptyGraphic?: boolean;
}

export interface MobileTableRowProps {
    id: string;
    title: ReactNode;
    meta?: ReactNode[];
    leading?: ReactNode;
    trailing?: ReactNode;
    selected?: boolean;
    selectable?: boolean;
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    onClick?: () => void;
}

export interface MobileTableProps {
    rows: MobileTableRowProps[];
    className?: string;
}

const cellStyle = (
    width?: number | string,
    minWidth?: number | string,
    align?: TableAlign,
): CSSProperties => ({
    width,
    minWidth,
    textAlign: align,
});

/**
 * Treez DS — Desktop table shell.
 *
 * Built from the Figma table families (`9966:52890`, `16701:105231`) with:
 * - 56px header row
 * - 56px body rows
 * - checkbox leading column
 * - selectable row state (green tint)
 * - horizontal scrolling when many columns are present
 */
export function Table<T>({
    rows,
    columns,
    getRowId,
    className,
    selectable = false,
    selectedRowIds = [],
    onSelectedRowIdsChange,
    onRowClick,
    scrollMaxHeight,
    stickyHeader = false,
    emptyMessage = "No data",
    emptyDescription,
    showEmptyGraphic = true,
}: TableProps<T>) {
    const selected = new Set(selectedRowIds);
    const allSelected =
        rows.length > 0 && rows.every(row => selected.has(getRowId(row)));

    const toggleAll = (checked: boolean) => {
        if (!onSelectedRowIdsChange) return;
        onSelectedRowIdsChange(checked ? rows.map(getRowId) : []);
    };

    const toggleOne = (rowId: string, checked: boolean) => {
        if (!onSelectedRowIdsChange) return;
        const next = new Set(selectedRowIds);
        if (checked) next.add(rowId);
        else next.delete(rowId);
        onSelectedRowIdsChange(Array.from(next));
    };

    const scrollBoxStyle =
        scrollMaxHeight != null
            ? {
                  maxHeight:
                      typeof scrollMaxHeight === "number"
                          ? `${scrollMaxHeight}px`
                          : scrollMaxHeight,
              }
            : undefined;

    const headCellClass = cx(
        styles.headCell,
        stickyHeader && styles.headCellSticky,
    );

    const emptyContent =
        typeof emptyMessage === "string" ? (
            <Message
                description={emptyDescription}
                graphic={
                    showEmptyGraphic ? (
                        <img
                            alt=""
                            className={styles.emptyGraphic}
                            src={messageIllustrations.tablePanel}
                        />
                    ) : undefined
                }
                title={emptyMessage}
            />
        ) : (
            emptyMessage
        );

    return (
        <div className={cx(styles.wrapper, className)} data-ui="table">
            <div
                className={styles.desktopScroll}
                data-scroll-y={scrollMaxHeight != null ? true : undefined}
                style={scrollBoxStyle}
            >
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {selectable ? (
                                <th
                                    className={cx(
                                        headCellClass,
                                        styles.selectHeadCell,
                                    )}
                                    scope="col"
                                >
                                    <Checkbox
                                        aria-label="Select all rows"
                                        checked={allSelected}
                                        onCheckedChange={toggleAll}
                                        showLabel={false}
                                    />
                                </th>
                            ) : null}
                            {columns.map(column => (
                                <th
                                    className={cx(
                                        headCellClass,
                                        column.className,
                                    )}
                                    key={column.id}
                                    scope="col"
                                    style={cellStyle(
                                        column.width,
                                        column.minWidth,
                                        column.align ?? "left",
                                    )}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td
                                    className={styles.emptyCell}
                                    colSpan={
                                        columns.length + (selectable ? 1 : 0)
                                    }
                                >
                                    {emptyContent}
                                </td>
                            </tr>
                        ) : null}
                        {rows.map(row => {
                            const rowId = getRowId(row);
                            const isSelected = selected.has(rowId);
                            return (
                                <tr
                                    className={styles.row}
                                    data-clickable={
                                        onRowClick ? true : undefined
                                    }
                                    data-selected={isSelected || undefined}
                                    key={rowId}
                                    onClick={
                                        onRowClick
                                            ? () => onRowClick(row)
                                            : undefined
                                    }
                                >
                                    {selectable ? (
                                        <td
                                            className={cx(
                                                styles.cell,
                                                styles.selectCell,
                                            )}
                                            onClick={event =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <Checkbox
                                                aria-label={`Select row ${rowId}`}
                                                checked={isSelected}
                                                onCheckedChange={checked =>
                                                    toggleOne(rowId, checked)
                                                }
                                                showLabel={false}
                                            />
                                        </td>
                                    ) : null}
                                    {columns.map(column => (
                                        <td
                                            className={cx(
                                                styles.cell,
                                                column.className,
                                            )}
                                            key={`${rowId}-${column.id}`}
                                            style={cellStyle(
                                                column.width,
                                                column.minWidth,
                                                column.align ?? "left",
                                            )}
                                        >
                                            {column.renderCell(row)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * Treez DS — Mobile table row list (`_Table/Mobile`).
 * One title row + one metadata row with dots between facets.
 */
export function MobileTable({ rows, className }: MobileTableProps) {
    return (
        <ul
            className={cx(styles.mobileList, styles.wrapper, className)}
            data-ui="mobile-table"
        >
            {rows.map(row => (
                <li
                    className={styles.mobileRow}
                    data-selected={row.selected || undefined}
                    key={row.id}
                    onClick={row.onClick}
                >
                    <div className={styles.mobileMain}>
                        {row.selectable ? (
                            <span
                                className={styles.mobileCheckboxWrap}
                                onClick={event => event.stopPropagation()}
                            >
                                <Checkbox
                                    aria-label={`Select row ${row.id}`}
                                    checked={row.checked}
                                    onCheckedChange={row.onCheckedChange}
                                    showLabel={false}
                                />
                            </span>
                        ) : null}
                        {row.leading}
                        <div className={styles.mobileBody}>
                            <span className={styles.mobileTitle}>
                                {row.title}
                            </span>
                            {row.meta && row.meta.length ? (
                                <span className={styles.mobileMeta}>
                                    {row.meta.map((item, index) => (
                                        <span key={`${row.id}-meta-${index}`}>
                                            {index > 0 ? (
                                                <span
                                                    className={styles.mobileDot}
                                                />
                                            ) : null}
                                            <span
                                                className={
                                                    styles.mobileMetaText
                                                }
                                            >
                                                {item}
                                            </span>
                                        </span>
                                    ))}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {row.trailing ? (
                        <span className={styles.mobileTrailing}>
                            {row.trailing}
                        </span>
                    ) : null}
                </li>
            ))}
        </ul>
    );
}
